import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, auth, getToken, setTokens, ApiError } from '@/services/api';
import type {
  Addon,
  BandwidthSnapshot,
  BankAccount,
  BuyCryptoResult,
  Computer,
  CryptoCurrency,
  CryptoWallet,
  GameServer,
  MiningScript,
  OwnedMiningScript,
  ScannedTarget,
  ScriptsCatalogResponse,
  SellCryptoResult,
  TransferResult,
  User,
} from '@/types/api';

export const useGameStore = defineStore('game', () => {
  // --- Raw state -------------------------------------------------------------
  const user = ref<User | null>(null);
  const token = ref<string | null>(getToken());
  const bankAccounts = ref<BankAccount[]>([]);
  const cryptoWallets = ref<CryptoWallet[]>([]);
  const cryptos = ref<CryptoCurrency[]>([]);
  const computers = ref<Computer[]>([]);
  const servers = ref<GameServer[]>([]);
  const addons = ref<Addon[]>([]);
  const targets = ref<ScannedTarget[]>([]);
  const miningScripts = ref<MiningScript[]>([]);
  const ownedScripts = ref<OwnedMiningScript[]>([]);
  // Bandwidth snapshot from the server. The store mirrors the DB's persisted
  // state; the view computes a "live" preview from this + Date.now() for
  // the visual bar.
  const bandwidth = ref<BandwidthSnapshot>({ current: 100, max: 100, lastUpdate: new Date().toISOString() });
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // --- Computed --------------------------------------------------------------
  // Treat the user as authenticated as soon as we have a token, so that a
  // hard reload of /dashboard doesn't bounce the user back to /login while
  // the user profile is being re-fetched. The router guard no longer blocks
  // the route, and the Dashboard's onMounted (or restoreSession) will refetch
  // the profile; if the token is invalid, the server returns 401 and we
  // clear the session then.
  const isAuthenticated = computed(() => !!token.value);

  const primaryBank = computed(
    () => bankAccounts.value.find((b) => b.isPrimary) ?? bankAccounts.value[0] ?? null,
  );

  /** Total credit balance across all bank accounts. */
  const totalCredits = computed(() =>
    bankAccounts.value.reduce((acc, b) => acc + BigInt(b.balance), 0n),
  );

  /** Sum of processing power with multiplicative addon bonuses applied. */
  const totalPower = computed(() => {
    const sum = (items: Array<{ processingPower: number; attachments?: { addon?: Addon }[] }>) =>
      items.reduce((acc, h) => {
        const mult = (h.attachments ?? []).reduce(
          (m, a) => m * (1 + (a.addon?.procBonus ?? 0)),
          1,
        );
        return acc + Math.floor(h.processingPower * mult);
      }, 0);
    return sum(computers.value) + sum(servers.value);
  });

  /** Sum of defense with multiplicative addon bonuses applied. */
  const totalDefense = computed(() => {
    const sum = (items: Array<{ defense: number; attachments?: { addon?: Addon }[] }>) =>
      items.reduce((acc, h) => {
        const mult = (h.attachments ?? []).reduce(
          (m, a) => m * (1 + (a.addon?.defenseBonus ?? 0)),
          1,
        );
        return acc + Math.floor(h.defense * mult);
      }, 0);
    return sum(computers.value) + sum(servers.value);
  });

  const totalHealth = computed(() =>
    [...computers.value, ...servers.value].reduce((acc, h) => acc + h.health, 0),
  );

  /**
   * The MiningScript the user currently has equipped, or null. Computed by
   * joining the owned rows with the catalog by scriptId. The service layer
   * enforces "at most one equipped" server-side; this computed reflects
   * whatever the server returned in the latest catalog fetch.
   */
  const equippedScript = computed<MiningScript | null>(() => {
    const equippedRow = ownedScripts.value.find((o) => o.equipped);
    if (!equippedRow) return null;
    return miningScripts.value.find((s) => s.id === equippedRow.scriptId) ?? null;
  });

  /**
   * Effective per-click credit multiplier (always >= 1). Defaults to 1.0
   * (the "Basic Miner" baseline) when nothing is equipped.
   */
  const currentMultiplier = computed<number>(() => equippedScript.value?.multiplier ?? 1);

  /** Per-click reward in credits, floored to an integer (server does the same). */
  const currentRewardPerClick = computed<number>(
    () => Math.floor(currentMultiplier.value),
  );

  // --- Auth ------------------------------------------------------------------
  async function register(email: string, password: string, handle: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const res = await auth.register(email, password, handle);
      user.value = res.user;
      token.value = res.token;
      setTokens(res.token, res.refreshToken);
      await loadAll();
    } finally {
      isLoading.value = false;
    }
  }

  async function login(email: string, password: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const res = await auth.login(email, password);
      user.value = res.user;
      token.value = res.token;
      setTokens(res.token, res.refreshToken);
      await loadAll();
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    await auth.logout();
    user.value = null;
    token.value = null;
    bankAccounts.value = [];
    cryptoWallets.value = [];
    computers.value = [];
    servers.value = [];
    targets.value = [];
    miningScripts.value = [];
    ownedScripts.value = [];
  }

  /**
   * Rehydrate the user profile from the backend using the persisted token.
   * Called once at app boot from main.ts (or from the router guard) so that
   * after a hard reload the store has `user` populated again, not just the
   * raw JWT. If the token is invalid, the server returns 401 and we clear
   * the local session.
   */
  async function restoreSession() {
    if (!token.value) return;
    try {
      // /auth/me is a tiny profile endpoint; if your backend doesn't expose
      // it, fall back to /economy/bank and use the inferred user id.
      const me = await api.get<User>('/auth/me');
      user.value = me;
    } catch (e) {
      // 401 -> token expired / tampered; wipe and force re-login.
      if (e instanceof ApiError && e.status === 401) {
        await logout();
      }
    }
  }

  // --- Data loaders ----------------------------------------------------------
  async function loadAll() {
    if (!token.value) return;
    await Promise.all([
      loadEconomy(),
      loadHardware(),
      loadCatalog(),
      loadTargets(),
      loadScripts(),
    ]);
  }

  async function loadEconomy() {
    const [banks, wallets, cryptoList] = await Promise.all([
      api.get<BankAccount[]>('/economy/bank'),
      api.get<CryptoWallet[]>('/economy/wallets'),
      api.get<CryptoCurrency[]>('/economy/crypto'),
    ]);
    bankAccounts.value = banks;
    cryptoWallets.value = wallets;
    cryptos.value = cryptoList;
  }

  async function loadHardware() {
    const [comps, srvs] = await Promise.all([
      api.get<Computer[]>('/hardware/computers'),
      api.get<GameServer[]>('/hardware/servers'),
    ]);
    computers.value = comps;
    servers.value = srvs;
  }

  async function loadCatalog() {
    addons.value = await api.get<Addon[]>('/hardware/addons');
  }

  async function loadTargets() {
    const res = await api.get<{ count: number; targets: ScannedTarget[] }>('/combat/scan');
    targets.value = res.targets;
  }

  async function loadScripts() {
    const res = await api.get<ScriptsCatalogResponse>('/hardware/scripts');
    miningScripts.value = res.catalog;
    ownedScripts.value = res.owned;
  }

  // --- Mutators --------------------------------------------------------------
  async function buyComputer(input: {
    name: string;
    processingPower: number;
    memory: number;
    storage: number;
    defense: number;
    paymentSource: { kind: 'BANK' | 'WALLET'; id: string };
  }) {
    const c = await api.post<Computer>('/hardware/computers', input);
    computers.value = [...computers.value, c];
    await loadEconomy(); // refresh balance after debit
    return c;
  }

  async function buyServer(input: {
    name: string;
    processingPower: number;
    memory: number;
    storage: number;
    defense: number;
    paymentSource: { kind: 'BANK' | 'WALLET'; id: string };
  }) {
    const s = await api.post<GameServer>('/hardware/servers', input);
    servers.value = [...servers.value, s];
    await loadEconomy();
    return s;
  }

  async function attachAddon(
    addonId: string,
    attachedToType: 'COMPUTER' | 'GAMESERVER',
    targetId: string,
  ) {
    await api.post('/hardware/attach', { addonId, attachedToType, targetId });
    await loadHardware();
  }

  async function scan() {
    await loadTargets();
  }

  /**
   * Purchase a mining script. Debits the chosen payment source on the
   * server (Serializable transaction); refreshes both the script catalog
   * and the economy state so the UI reflects the new ownership + balance
   * in one tick.
   */
  async function buyScript(
    scriptId: string,
    paymentSource: { kind: 'BANK' | 'WALLET'; id: string },
  ) {
    const result = await api.post<{ scriptId: string; cost: string; multiplier: number }>(
      '/economy/scripts/buy',
      { scriptId, paymentSource },
    );
    await Promise.all([loadScripts(), loadEconomy()]);
    return result;
  }

  /**
   * Equip a script the user already owns, or unequip the current one
   * (pass `null`). The server enforces ownership and the "exactly one
   * equipped" invariant.
   */
  async function equipScript(scriptId: string | null) {
    await api.post<{ equippedScriptId: string | null }>('/economy/scripts/equip', {
      scriptId,
    });
    await loadScripts();
  }

  // --- Banking ---------------------------------------------------------------
  /**
   * Open a new bank account under the current user. The server accepts an
   * optional `initialBalance` and `isPrimary`. Refreshing `loadEconomy`
   * pulls the new row + balance so the UI updates in one tick.
   */
  async function openAccount(input: {
    name: string;
    initialBalance?: string;
    isPrimary?: boolean;
  }) {
    const account = await api.post<BankAccount>('/economy/bank', input);
    bankAccounts.value = [...bankAccounts.value, account];
    return account;
  }

  /**
   * Wire transfer between any two bank accounts in the system. The server
   * enforces that `fromAccountId` belongs to the caller; the destination can
   * be any account (own or another user). Refreshes economy state on success.
   */
  async function transfer(input: {
    fromAccountId: string;
    toAccountId: string;
    amount: string;
  }) {
    const result = await api.post<TransferResult>('/economy/bank/transfer', input);
    await loadEconomy();
    return result;
  }

  // --- Crypto trade ----------------------------------------------------------
  /**
   * Buy crypto at the current authoritative price. The server computes the
   * floor of `fiatAmount / price` as the units credited to the wallet and
   * refunds any untradeable remainder to the source bank. Both balances are
   * refreshed on success.
   */
  async function buyCrypto(input: {
    bankAccountId: string;
    walletId: string;
    fiatAmount: string;
  }) {
    const result = await api.post<BuyCryptoResult>('/economy/trade/crypto/buy', input);
    await loadEconomy();
    return result;
  }

  /**
   * Sell crypto at the current authoritative price. The server credits
   * `units * price` to the destination bank. Refreshes both balances.
   */
  async function sellCrypto(input: {
    bankAccountId: string;
    walletId: string;
    cryptoAmount: string;
  }) {
    const result = await api.post<SellCryptoResult>('/economy/trade/crypto/sell', input);
    await loadEconomy();
    return result;
  }

  async function attack(targetType: 'REAL_USER' | 'NPC_COMPANY' | 'BOT', targetId: string) {
    const result = await api.post<{
      attackId: string;
      outcome: 'VICTORY' | 'DEFEAT' | 'CRITICAL' | 'REPELLED' | 'BACKHACK';
      lootStolen: string;
      damageDealt: number;
      bandwidthSpent: number;
      bandwidthRemaining: number;
      backhackLoss: string;
    }>('/combat/attack', { targetType, targetId });
    // Optimistically reflect the bandwidth debit so the bar updates without
    // a follow-up roundtrip; an error or a later refetch will reconcile.
    bandwidth.value = {
      current: result.bandwidthRemaining,
      max: bandwidth.value.max,
      lastUpdate: new Date().toISOString(),
    };
    // Reload to get the new balances and any hardware damage
    await loadEconomy();
    await loadHardware();
    return result;
  }

  /**
   * Fetch the attacker's live bandwidth (after passive regen since last
   * update). Cheap, idempotent; intended for polling on the Attack Console.
   */
  async function refreshBandwidth() {
    const snap = await api.get<BandwidthSnapshot>('/combat/bandwidth');
    bandwidth.value = snap;
    return snap;
  }

  /**
   * Apply the server's authoritative state to the local store after a
   * successful mine flush. The `multiplier` reported by the server is the
   * value it actually used to credit the batch — we stash it on the primary
   * bank row's `balance` (overwriting the optimistic value), and update
   * `confirmedEarned` is done by the batcher (it owns that counter).
   */
  function applyMinedClicks(_clicks: number, newBalance: string) {
    if (primaryBank.value) {
      primaryBank.value.balance = newBalance;
    }
  }

  return {
    // state
    user,
    token,
    bankAccounts,
    cryptoWallets,
    cryptos,
    computers,
    servers,
    addons,
    targets,
    miningScripts,
    ownedScripts,
    bandwidth,
    isLoading,
    error,
    // computed
    isAuthenticated,
    primaryBank,
    totalCredits,
    totalPower,
    totalDefense,
    totalHealth,
    equippedScript,
    currentMultiplier,
    currentRewardPerClick,
    // auth
    register,
    login,
    logout,
    restoreSession,
    // loaders
    loadAll,
    loadEconomy,
    loadHardware,
    loadCatalog,
    loadTargets,
    loadScripts,
    // mutators
    buyComputer,
    buyServer,
    attachAddon,
    buyScript,
    equipScript,
    openAccount,
    transfer,
    buyCrypto,
    sellCrypto,
    scan,
    attack,
    refreshBandwidth,
    applyMinedClicks,
  };
});
